import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { salesApi, accountApi, userApi } from '../../api';
import { copyToClipboard } from '../../utils/clipboard';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Table, Pagination, Modal, FormItem, message, confirm, formatDate } from '../../components/common';

const AdminSales = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });

  // 从 URL 参数初始化 filters
  const initialKeyword = searchParams.get('keyword') || '';
  const [filters, setFilters] = useState({
    is_delivered: '',
    is_settled: '',
    keyword: initialKeyword,
    sales_id: initialKeyword ? '' : user.id,  // 如果有关键词搜索，清空销售人筛选以便找到记录
    developer_id: '',  // 新增：所属人筛选
    remark: ''  // 新增：备注筛选
  });
  const [sort, setSort] = useState({ field: 'created_at', order: 'desc' });
  const [modalVisible, setModalVisible] = useState(false);
  const [bindModalVisible, setBindModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [bindingRecord, setBindingRecord] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '', customer_gender: '', customer_age: '', customer_phone: '', customer_email: '', amount: '', sales_id: '', remark: ''
  });
  // 绑定账号搜索筛选相关状态
  const [accountSearchKeyword, setAccountSearchKeyword] = useState('');
  const [accountDeveloperId, setAccountDeveloperId] = useState('');
  const [owners, setOwners] = useState([]);
  const amountOptions = [149, 150, 100, 80, 50];

  // 结算相关状态
  const [settleModalVisible, setSettleModalVisible] = useState(false);
  const [settlingRecord, setSettlingRecord] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters,
        sort_by: sort.field,
        order: sort.order
      };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await salesApi.getList(params);
      setRecords(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesUsers = async () => {
    try {
      if (user.role && user.role.includes('admin')) {
        // 管理员可以选择所有活跃用户
        const res = await userApi.getList({ per_page: 1000, status: 1 });
        setSalesUsers(res.data.items);
      } else {
        // 非管理员只能选择自己
        setSalesUsers([user]);
      }
    } catch (err) {
      console.error(err);
      message.error('获取用户列表失败');
    }
  };

  const fetchAvailableAccounts = async (keyword = '', developerId = '') => {
    try {
      const params = {};
      if (keyword) params.keyword = keyword;
      if (developerId) params.developer_id = developerId;
      const res = await accountApi.getAvailable(params);
      setAvailableAccounts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // 获取开发者/管理员列表
  const fetchOwners = async () => {
    try {
      const res = await accountApi.getOwners();
      setOwners(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchSalesUsers(); fetchOwners(); }, []);
  useEffect(() => { fetchRecords(); }, [pagination.page, pagination.per_page, sort]);

  // 如果有 URL 参数 keyword，页面加载后自动搜索
  useEffect(() => {
    if (initialKeyword) {
      fetchRecords();
    }
  }, []);  // 只在首次加载时执行

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchRecords();
  };

  const handleSort = (field, order) => {
    setSort({ field, order });
  };

  const handleCreate = () => {
    setEditingRecord(null);
    setFormData({
      customer_name: '', customer_gender: '', customer_age: '', customer_phone: '', customer_email: '',
      amount: '',
      amount: '',
      sales_id: user.id || '', // 默认为当前用户
      remark: ''
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`确定删除该销售记录吗？`)) return;
    try {
      await salesApi.delete(record.id);
      message.success('删除成功');
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingRecord) {
        await salesApi.update(editingRecord.id, formData);
        message.success('更新成功');
      } else {
        await salesApi.create(formData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleBind = async (record) => {
    setBindingRecord(record);
    setAccountSearchKeyword('');
    setAccountDeveloperId('');
    await fetchAvailableAccounts();
    setSelectedAccountId('');
    setBindModalVisible(true);
  };

  // 账号搜索
  const handleAccountSearch = () => {
    fetchAvailableAccounts(accountSearchKeyword, accountDeveloperId);
  };

  const handleUnbind = async (record) => {
    if (!window.confirm('确定解绑该账号吗？')) return;
    try {
      await salesApi.unbind(record.id);
      message.success('解绑成功');
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleBindSubmit = async () => {
    if (!selectedAccountId) {
      message.error('请选择要绑定的账号');
      return;
    }
    try {
      await salesApi.bind(bindingRecord.id, parseInt(selectedAccountId));
      message.success('绑定成功');
      setBindModalVisible(false);
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDeliver = async (record) => {
    try {
      await salesApi.markDelivered(record.id);
      message.success('邮件发送成功，已标记为发号');
      fetchRecords();
    } catch (err) {
      message.error(err.message || '发号失败');
    }
  };

  const handleOpenSettle = (record) => {
    setSettlingRecord(record);
    // 默认实结金额：如果已有则显示，否则显示销售金额
    setSettleAmount(record.settled_amount || record.amount);
    setSettleModalVisible(true);
  };

  const handleSettleSubmit = async () => {
    if (!settleAmount && settleAmount !== 0) {
      message.error('请输入实结金额');
      return;
    }
    const amount = parseFloat(settleAmount);
    if (isNaN(amount) || amount < 0) {
      message.error('金额无效');
      return;
    }
    if (amount > settlingRecord.amount) {
      message.error('实结金额不能大于销售金额');
      return;
    }

    try {
      await salesApi.markSettled(settlingRecord.id, { settled_amount: amount });
      message.success('已标记为结算');
      setSettleModalVisible(false);
      fetchRecords();
    } catch (err) {
      message.error(err.message);
    }
  };

  const applyDiscount = (rate) => {
    if (settlingRecord) {
      setSettleAmount((settlingRecord.amount * rate).toFixed(2));
    }
  };

  const handleCopyDeliveryInfo = async (record) => {
    if (!record.google_account) {
      message.error('未绑定账号');
      return;
    }
    const account = record.google_account;
    const text = `谷歌邮箱： ${account.email}
密码： ${account.password || '-'}
2FA通行码： ${account.two_factor_code || '-'}
验证邮箱： ${account.recovery_email || '-'}`;

    const success = await copyToClipboard(text);
    if (success) {
      message.success('发货信息已复制到剪贴板:\n' + text);
    } else {
      message.error('复制失败，请重试');
    }
  };

  const columns = [
    { key: 'id', title: 'ID', width: '60px', sorter: true },
    { key: 'customer_name', title: '客户名称', render: (v) => v || '-', sorter: true },
    { key: 'customer_email', title: '客户邮箱', sorter: true },
    { key: 'customer_phone', title: '手机号', render: (v) => v || '-' },
    { key: 'customer_gender', title: '性别', render: (v) => v === 'male' ? '男' : v === 'female' ? '女' : '-' },
    { key: 'customer_age', title: '年龄', render: (v) => v || '-' },
    { key: 'amount', title: '金额', render: (v) => `¥${v.toFixed(2)}`, sorter: true },
    { key: 'settled_amount', title: '实结', render: (v) => v ? `¥${v.toFixed(2)}` : '-' },
    { key: 'sales_name', title: '销售人员' },
    { key: 'owner_name', title: '所属人', render: (v) => v || <span style={{ color: '#999' }}>-</span> },
    { key: 'google_account', title: '绑定账号', render: (v) => v ? v.email : <span className="tag tag-warning">未绑定</span> },
    { key: 'is_delivered', title: '发号状态', render: (v) => <span className={`tag ${v ? 'tag-success' : 'tag-default'}`}>{v ? '已发号' : '未发号'}</span>, sorter: true },
    { key: 'is_settled', title: '结算状态', render: (v) => <span className={`tag ${v ? 'tag-success' : 'tag-default'}`}>{v ? '已结算' : '待结算'}</span>, sorter: true },
    { key: 'remark', title: '备注', render: (v) => v ? <span title={v} style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{v}</span> : '-' },
    { key: 'created_at', title: '创建时间', render: (v) => formatDate(v), sorter: true },
    {
      key: 'actions', title: '操作', width: '220px', render: (_, row) => (
        <div className="action-buttons">
          <button className="btn btn-small btn-default" onClick={() => handleEdit(row)}>编辑</button>
          {!row.google_account_id ? (
            <button className="btn btn-small btn-primary" onClick={() => handleBind(row)}>绑定</button>
          ) : (
            <>
              <button className="btn btn-small btn-warning" onClick={() => handleUnbind(row)}>解绑</button>
              <button className="btn btn-small btn-default" onClick={() => handleCopyDeliveryInfo(row)}>发货信息</button>
              <button className="btn btn-small btn-success" onClick={() => handleDeliver(row)}>
                {row.is_delivered ? '重发' : '发号'}
              </button>
            </>
          )}

          <button className="btn btn-small btn-success" onClick={() => handleOpenSettle(row)}>
            {row.is_settled ? '重结' : '结算'}
          </button>

          <button className="btn btn-small btn-danger" onClick={() => handleDelete(row)}>删除</button>
        </div>
      )
    }
  ];

  return (
    <div>
      <PageHeader title="销售记录管理" extra={<button className="btn btn-primary" onClick={handleCreate}>新建记录</button>} />

      <div className="search-bar" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>客户搜索</label>
          <input placeholder="名称/邮箱" value={filters.keyword} onChange={e => setFilters({ ...filters, keyword: e.target.value })} style={{ width: '120px' }} />
        </div>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>销售人员</label>
          <select value={filters.sales_id} onChange={e => setFilters({ ...filters, sales_id: e.target.value })}>
            <option value="">全部</option>
            {salesUsers.map(s => <option key={s.id} value={s.id}>{s.real_name || s.username}</option>)}
          </select>
        </div>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>所属人</label>
          <select value={filters.developer_id} onChange={e => setFilters({ ...filters, developer_id: e.target.value })}>
            <option value="">全部</option>
            {owners.map(o => <option key={o.id} value={o.id}>{o.real_name || o.username}</option>)}
          </select>
        </div>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>发号状态</label>
          <select value={filters.is_delivered} onChange={e => setFilters({ ...filters, is_delivered: e.target.value })}>
            <option value="">全部</option>
            <option value="true">已发号</option>
            <option value="false">未发号</option>
          </select>
        </div>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>结算状态</label>
          <select value={filters.is_settled} onChange={e => setFilters({ ...filters, is_settled: e.target.value })}>
            <option value="">全部</option>
            <option value="true">已结算</option>
            <option value="false">待结算</option>
          </select>
        </div>
        <div className="form-item">
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>备注搜索</label>
          <input placeholder="搜索备注" value={filters.remark} onChange={e => setFilters({ ...filters, remark: e.target.value })} style={{ width: '100px' }} />
        </div>
        <div className="form-item" style={{ alignSelf: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSearch}>搜索</button>
        </div>
      </div>

      <Table
        columns={columns}
        data={records}
        loading={loading}
        sort={sort}
        onSort={handleSort}
      />
      <Pagination current={pagination.page} total={pagination.total} pageSize={pagination.per_page}
        onChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(size) => setPagination(prev => ({ ...prev, per_page: size, page: 1 }))} />

      {/* 编辑/新建模态框 */}
      <Modal visible={modalVisible} title={editingRecord ? '编辑销售记录' : '新建销售记录'} onClose={() => setModalVisible(false)}
        footer={<><button className="btn btn-default" onClick={() => setModalVisible(false)}>取消</button>
          <button className="btn btn-primary" onClick={handleSubmit}>确定</button></>}>
        <FormItem label="客户名称">
          <input value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} placeholder="选填" />
        </FormItem>
        <FormItem label="客户邮箱" required>
          <input type="email" value={formData.customer_email || ''} onChange={e => setFormData({ ...formData, customer_email: e.target.value })} placeholder="必填" />
        </FormItem>
        <FormItem label="客户手机号">
          <input value={formData.customer_phone || ''} onChange={e => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="选填" />
        </FormItem>
        <FormItem label="客户性别">
          <select value={formData.customer_gender || ''} onChange={e => setFormData({ ...formData, customer_gender: e.target.value })}>
            <option value="">请选择</option>
            <option value="male">男</option>
            <option value="female">女</option>
          </select>
        </FormItem>
        <FormItem label="客户年龄">
          <input type="number" value={formData.customer_age || ''} onChange={e => setFormData({ ...formData, customer_age: e.target.value ? parseInt(e.target.value) : '' })} placeholder="选填" />
        </FormItem>
        <FormItem label="销售金额" required>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) || '' })} style={{ width: '120px' }} placeholder="输入金额" />
            <span style={{ color: '#666' }}>或选择：</span>
            {amountOptions.map(opt => (
              <button key={opt} type="button" className={`btn btn-small ${formData.amount === opt ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setFormData({ ...formData, amount: opt })}>¥{opt}</button>
            ))}
          </div>
        </FormItem>
        <FormItem label="销售人员" required>
          <select
            value={formData.sales_id}
            onChange={e => setFormData({ ...formData, sales_id: parseInt(e.target.value) })}
            disabled={!user.role?.includes('admin')}
          >
            {salesUsers.map(s => <option key={s.id} value={s.id}>{s.real_name || s.username}</option>)}
          </select>
        </FormItem>
        <FormItem label="备注">
          <textarea value={formData.remark || ''} onChange={e => setFormData({ ...formData, remark: e.target.value })} />
        </FormItem>
      </Modal>

      {/* 绑定账号模态框 */}
      <Modal visible={bindModalVisible} title="绑定谷歌账号" onClose={() => setBindModalVisible(false)}
        footer={<><button className="btn btn-default" onClick={() => setBindModalVisible(false)}>取消</button>
          <button className="btn btn-primary" onClick={handleBindSubmit}>确定绑定</button></>}>

        {/* 搜索和筛选区域 */}
        <div className="search-bar" style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input placeholder="搜索邮箱" value={accountSearchKeyword}
            onChange={e => setAccountSearchKeyword(e.target.value)}
            style={{ flex: 1, minWidth: '150px' }} />
          <select value={accountDeveloperId} onChange={e => setAccountDeveloperId(e.target.value)} style={{ minWidth: '140px' }}>
            <option value="">全部开发者/管理员</option>
            {owners.map(o => <option key={o.id} value={o.id}>{o.real_name || o.username}</option>)}
          </select>
          <button className="btn btn-primary" onClick={handleAccountSearch}>搜索</button>
        </div>

        {/* 账号列表展示 */}
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {availableAccounts.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>暂无待销售账号</p>
          ) : (
            availableAccounts.map(a => (
              <div key={a.id}
                onClick={() => setSelectedAccountId(String(a.id))}
                style={{
                  padding: '12px', border: '1px solid #e8e8e8',
                  borderRadius: '8px', marginBottom: '8px', cursor: 'pointer',
                  background: selectedAccountId === String(a.id) ? '#e6f7ff' : '#fff',
                  borderColor: selectedAccountId === String(a.id) ? '#1890ff' : '#e8e8e8'
                }}>
                <div style={{ fontWeight: 'bold', color: '#333' }}>{a.email}</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  所属: {a.developer_name || '-'} | 创建: {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* 结算模态框 */}
      <Modal visible={settleModalVisible} title="结算订单" onClose={() => setSettleModalVisible(false)}
        footer={<><button className="btn btn-default" onClick={() => setSettleModalVisible(false)}>取消</button>
          <button className="btn btn-primary" onClick={handleSettleSubmit}>确定结算</button></>}>
        {settlingRecord && (
          <div>
            <div style={{ marginBottom: '15px' }}>
              <strong>销售金额：</strong> ¥{settlingRecord.amount.toFixed(2)}
            </div>
            <FormItem label="实结金额" required>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="number"
                  step="0.01"
                  value={settleAmount}
                  onChange={e => setSettleAmount(e.target.value)}
                  style={{ width: '150px' }}
                  placeholder="输入实结金额"
                />
                <span style={{ fontSize: '12px', color: '#999' }}>
                  (需 ≤ {settlingRecord.amount.toFixed(2)})
                </span>
              </div>
            </FormItem>
            <FormItem label="快捷折扣">
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {[0.9, 0.8, 0.7, 0.6, 0.5].map(rate => (
                  <button
                    key={rate}
                    className="btn btn-small btn-default"
                    onClick={() => applyDiscount(rate)}
                  >
                    {rate * 10}折
                  </button>
                ))}
              </div>
            </FormItem>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminSales;
